import type { SearchIconName } from "./searchIcons.ts";

export interface PoiCategory {
  id: string;
  label: string;
  // Stored as a string so this data stays importable from plain Deno tests.
  // The type comes from the shared icon registry, which keeps the allowed names
  // narrow without importing Svelte components at runtime here.
  icon: SearchIconName;
  aliases: readonly string[];
  tagFilters: readonly {
    key: string;
    value: string;
  }[];
}

export const POI_CATEGORIES = [
  {
    id: "restaurants",
    label: "Restaurants",
    icon: "Utensils",
    aliases: ["restaurant", "food", "dining", "places to eat", "food court"],
    tagFilters: [
      { key: "amenity", value: "restaurant" },
      { key: "amenity", value: "food_court" },
    ],
  },
  {
    id: "cafes",
    label: "Cafes",
    icon: "Coffee",
    aliases: ["cafe", "coffee", "coffee shops"],
    tagFilters: [
      { key: "amenity", value: "cafe" },
    ],
  },
  {
    id: "bakeries",
    label: "Bakeries",
    icon: "Croissant",
    aliases: ["bakery", "bread", "pastry"],
    tagFilters: [
      { key: "shop", value: "bakery" },
    ],
  },
  {
    id: "bars",
    label: "Bars, pubs",
    icon: "Beer",
    aliases: ["bar", "pub", "nightlife"],
    tagFilters: [
      { key: "amenity", value: "bar" },
      { key: "amenity", value: "pub" },
    ],
  },
  {
    id: "fast_food",
    label: "Fast Food",
    icon: "Hamburger",
    aliases: ["fast food", "takeaway"],
    tagFilters: [
      { key: "amenity", value: "fast_food" },
    ],
  },
  {
    id: "ice_cream",
    label: "Ice Cream",
    icon: "IceCreamCone",
    aliases: ["ice cream", "gelato", "frozen yogurt"],
    tagFilters: [
      { key: "amenity", value: "ice_cream" },
    ],
  },
  {
    id: "supermarkets",
    label: "Supermarkets",
    icon: "ShoppingCart",
    aliases: ["supermarket", "grocery store", "groceries"],
    tagFilters: [
      { key: "shop", value: "supermarket" },
      { key: "shop", value: "wholesale" },
    ],
  },
  {
    id: "convenience_stores",
    label: "Convenience Stores",
    icon: "ShoppingBasket",
    aliases: ["convenience store", "corner shop", "mini-mart"],
    tagFilters: [
      { key: "shop", value: "convenience" },
    ],
  },
  {
    id: "landmarks",
    label: "Landmarks",
    icon: "Landmark",
    aliases: [
      "landmark",
      "landmarks",
      "monument",
      "memorial",
      "attraction",
      "viewpoint",
      "statue",
    ],
    tagFilters: [
      { key: "historic", value: "monument" },
      { key: "historic", value: "memorial" },
      { key: "tourism", value: "attraction" },
      { key: "tourism", value: "artwork" },
      { key: "tourism", value: "viewpoint" },
    ],
  },
  {
    id: "museums",
    label: "Museums",
    icon: "Landmark",
    aliases: ["museum", "museums", "art museum", "culture"],
    tagFilters: [
      { key: "tourism", value: "museum" },
      { key: "building", value: "museum" },
    ],
  },
  {
    id: "gyms",
    label: "Gyms",
    icon: "Dumbbell",
    aliases: ["gym", "fitness", "fitness centers"],
    tagFilters: [
      { key: "leisure", value: "fitness_centre" },
      { key: "amenity", value: "gym" },
    ],
  },
  {
    id: "pharmacies",
    label: "Pharmacies",
    icon: "Pill",
    aliases: ["pharmacy", "chemist", "drugstores"],
    tagFilters: [
      { key: "amenity", value: "pharmacy" },
      { key: "shop", value: "chemist" },
    ],
  },
  {
    id: "drinking_fountains",
    label: "Drinking Fountains",
    icon: "Droplet",
    aliases: [
      "drinking fountain",
      "drinking water",
      "public water",
      "water point",
    ],
    tagFilters: [
      { key: "amenity", value: "drinking_water" },
      { key: "drinking_water", value: "yes" },
      { key: "amenity", value: "water_point" },
    ],
  },
  {
    id: "playgrounds",
    label: "Playgrounds",
    icon: "Castle",
    aliases: ["playground", "play area", "kids playground"],
    tagFilters: [
      { key: "leisure", value: "playground" },
    ],
  },
  {
    id: "public_bookcases",
    label: "Public Bookcases",
    icon: "LibraryBig",
    aliases: ["public bookcase", "book exchange", "little free library"],
    tagFilters: [
      { key: "amenity", value: "public_bookcase" },
    ],
  },
  {
    id: "hotels",
    label: "Hotels",
    icon: "Hotel",
    aliases: ["hotel", "accommodation", "lodging"],
    tagFilters: [
      { key: "tourism", value: "hotel" },
      { key: "building", value: "hotel" },
    ],
  },
  {
    id: "libraries",
    label: "Libraries",
    icon: "Landmark",
    aliases: ["library", "public library", "book lending"],
    tagFilters: [
      { key: "amenity", value: "library" },
    ],
  },
] as const satisfies readonly PoiCategory[];

export const FEATURED_POI_CATEGORY_IDS = [
  "restaurants",
  "cafes",
  "supermarkets",
  "landmarks",
  "museums",
  "bars",
] as const satisfies readonly PoiCategory["id"][];
